import React, { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useAudio } from "../../lib/audio";

// Compact speaker toggle + hover volume slider.
// Placed in the site header. Muted by default; user must click to enable audio
// (browser autoplay rules).
export default function AudioToggle() {
  const { enabled, volume, toggle, setVolume } = useAudio();
  const [showSlider, setShowSlider] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  const onEnter = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setShowSlider(true);
  };
  const onLeave = () => {
    hideTimer.current = setTimeout(() => setShowSlider(false), 300);
  };

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid="audio-toggle-wrapper"
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={enabled ? "Mute site music" : "Play site music"}
        aria-pressed={enabled}
        data-testid="audio-toggle-btn"
        className={`w-9 h-9 flex items-center justify-center border transition-colors ${
          enabled
            ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10 hover:bg-[#C5A059]/20"
            : "border-white/15 text-[#A39E99] hover:border-[#C5A059]/60 hover:text-[#C5A059]"
        }`}
      >
        {enabled ? <Volume2 size={15} strokeWidth={1.6} /> : <VolumeX size={15} strokeWidth={1.6} />}
      </button>

      {/* Vertical volume slider — only visible when enabled + hovered */}
      {enabled && showSlider && (
        <div
          className="absolute top-full right-0 mt-2 px-3 py-4 bg-[#0A0A0A] border border-[#C5A059]/30 shadow-2xl z-50"
          data-testid="audio-volume-panel"
        >
          <div className="flex items-center gap-2">
            <VolumeX size={12} className="text-[#A39E99]" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              aria-label="Music volume"
              data-testid="audio-volume-slider"
              className="w-28 accent-[#C5A059] cursor-pointer"
            />
            <Volume2 size={12} className="text-[#C5A059]" />
          </div>
        </div>
      )}
    </div>
  );
}
